import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play } from "lucide-react";
import { getVisuals } from "../lib/api";
import { Reveal } from "../components/Motion";
import VisualLightbox from "../components/VisualLightbox";

export default function Visuals() {
  const [visuals, setVisuals] = useState([]);
  const [lightbox, setLightbox] = useState(null); // index into ordered list

  useEffect(() => {
    document.title = "Visuals — Motion & Meaning";
    getVisuals().then(setVisuals).catch(() => {});
  }, []);

  const featured = visuals.find((v) => v.featured);
  const rest = visuals.filter((v) => !v.featured);
  const ordered = featured ? [featured, ...rest] : rest;

  return (
    <div className="pt-32 md:pt-44 pb-32" data-testid="visuals-page">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <Reveal>
          <p className="font-mono text-[11px] tracking-[0.4em] text-gold mb-6">THE ARCHIVE</p>
          <h1 className="font-serif text-6xl sm:text-7xl lg:text-9xl font-light leading-none text-white">VISUALS</h1>
          <p className="text-white/50 max-w-xl mt-8 leading-relaxed">
            Music videos, visualizers, trailers and cinematic experiments. Where the dimensions become visible.
          </p>
        </Reveal>

        {featured && (
          <Reveal>
            <button
              onClick={() => setLightbox(0)}
              className="mt-20 group relative overflow-hidden aspect-video cursor-pointer block w-full text-left"
              data-testid={`visual-open-${featured.id}`}
            >
              <motion.img
                src={featured.thumbnail}
                alt={featured.title}
                className="w-full h-full object-cover"
                whileHover={{ scale: 1.04 }}
                transition={{ duration: 1.2 }}
              />
              <div className="absolute inset-0 bg-black/40 group-hover:bg-black/25 transition-colors duration-500" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-20 h-20 rounded-full border border-white/50 flex items-center justify-center group-hover:border-gold group-hover:scale-110 transition-all duration-500">
                  <Play className="text-white group-hover:text-gold ml-1" size={26} />
                </div>
              </div>
              <div className="absolute bottom-0 left-0 p-8 md:p-12">
                <p className="font-mono text-[11px] tracking-[0.3em] text-gold mb-3">{featured.type.toUpperCase()} · {featured.date}</p>
                <h2 className="font-serif text-4xl md:text-6xl font-light text-white">{featured.title}</h2>
              </div>
            </button>
          </Reveal>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-20">
          {rest.map((v, i) => (
            <Reveal key={v.id} delay={i * 0.08}>
              <button
                onClick={() => setLightbox(ordered.indexOf(v))}
                className="group cursor-pointer text-left w-full"
                data-testid={`visual-open-${v.id}`}
              >
                <div className="relative overflow-hidden aspect-video">
                  <motion.img
                    src={v.thumbnail}
                    alt={v.title}
                    className="w-full h-full object-cover"
                    whileHover={{ scale: 1.06 }}
                    transition={{ duration: 1 }}
                  />
                  <div className="absolute inset-0 bg-black/30 group-hover:bg-black/10 transition-colors duration-500" />
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    <Play className="text-white" size={32} />
                  </div>
                </div>
                <p className="font-mono text-[10px] tracking-[0.25em] text-gold mt-5">{v.type.toUpperCase()} · {v.date}</p>
                <h3 className="font-serif text-2xl font-light text-white mt-2 group-hover:text-gold transition-colors">{v.title}</h3>
                <p className="text-white/50 text-sm mt-1">{v.description}</p>
              </button>
            </Reveal>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {lightbox !== null && (
          <VisualLightbox
            visuals={ordered}
            index={lightbox}
            onClose={() => setLightbox(null)}
            onNavigate={setLightbox}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
